import debounce from 'lodash.debounce';
import {useEffect, useMemo, useRef} from 'react';

type UseUserActivityOptions = {
  debounceMs?: number;
  leading?: boolean;
  maxWaitMs?: number;
  eventsOverride?: string[];
};

const DEFAULT_EVENTS = ['mousemove', 'mousedown', 'click', 'scroll', 'wheel', 'keydown', 'keypress', 'keyup', 'touchstart'];

export function useUserActivity(
  onActivity: () => void,
  {debounceMs = 5000, leading = true, maxWaitMs, eventsOverride}: UseUserActivityOptions = {}
) {
  const events = useMemo(() => eventsOverride ?? DEFAULT_EVENTS, [eventsOverride]);

  const onActivityRef = useRef(onActivity);

  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  const handler = useMemo(
    () =>
      debounce(() => onActivityRef.current(), debounceMs, {
        leading,
        trailing: true,
        maxWait: maxWaitMs,
      }),
    [debounceMs, leading, maxWaitMs]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    events.forEach((event) => window.addEventListener(event, handler, {passive: true}));

    // eslint-disable-next-line consistent-return
    return () => {
      events.forEach((event) => window.removeEventListener(event, handler));
      handler.cancel();
    };
  }, [events, handler]);
}
